/**
 * Session ids and API keys — docs/PROTOCOL.md §8, §9.
 *
 * Uses WebCrypto (`globalThis.crypto`), available in Node 22 and every target
 * browser, so this module stays isomorphic.
 */
import { LIMITS } from './limits.js';
/** Base58: base62 minus the four glyph pairs people mistype when reading a link aloud. */
const BASE58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
const BASE62 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
/**
 * Draw `length` characters from `alphabet` without modulo bias.
 *
 * Rejection sampling: bytes at or above the largest whole multiple of the alphabet
 * size are discarded rather than folded, which would over-represent the first
 * `256 % alphabet.length` characters.
 */
function randomString(length, alphabet) {
    const limit = Math.floor(256 / alphabet.length) * alphabet.length;
    let out = '';
    const buf = new Uint8Array(length * 2);
    while (out.length < length) {
        crypto.getRandomValues(buf);
        for (let i = 0; i < buf.length && out.length < length; i++) {
            const byte = buf[i];
            if (byte < limit)
                out += alphabet[byte % alphabet.length];
        }
    }
    return out;
}
export function generateSessionId() {
    return randomString(LIMITS.SESSION_ID_LENGTH, BASE58);
}
export function isValidSessionId(id) {
    if (typeof id !== 'string' || id.length !== LIMITS.SESSION_ID_LENGTH)
        return false;
    for (const ch of id)
        if (!BASE58.includes(ch))
            return false;
    return true;
}
export function generateApiKey() {
    return LIMITS.API_KEY_PREFIX + randomString(LIMITS.API_KEY_RANDOM_LENGTH, BASE62);
}
export function isWellFormedApiKey(key) {
    return (typeof key === 'string' &&
        key.startsWith(LIMITS.API_KEY_PREFIX) &&
        key.length === LIMITS.API_KEY_PREFIX.length + LIMITS.API_KEY_RANDOM_LENGTH);
}
/** Lowercase hex SHA-256. Only the digest of an API key is ever stored. */
export async function sha256Hex(input) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
}
/**
 * Constant-time string comparison, for passcodes and key digests.
 *
 * Length is compared up front — that leaks only the length, which for a digest is
 * fixed anyway, and for a passcode is far less useful to an attacker than the
 * character-by-character timing that `===` would expose.
 */
export function timingSafeEqual(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
    return diff === 0;
}
//# sourceMappingURL=ids.js.map